import type {
  ExecuteProposalRequest,
  KanbanProposal,
} from '../core/contracts/proposals.js';
import type { ChannelBindingRegistry } from '../agents/channel-binding-registry.js';
import type { ProposalService } from './proposal-service.js';
import type { KanbanProvider, KanbanTask } from '../plugins/kanban/kanban-provider.js';
import type { AuditLogger } from '../agents/audit-log.js';
import type { KanbanTaskId } from '../core/contracts/channel-agent.js';

export interface ExecutionResult {
  proposalId: string;
  idempotencyKey: string;
  executedAt: number;
  task?: KanbanTask;
  cached?: boolean;
}

export class ProposalExecutor {
  private executedKeys = new Map<string, ExecutionResult>();

  constructor(
    private bindingRegistry: ChannelBindingRegistry,
    private proposalService: ProposalService,
    private kanbanProvider: KanbanProvider,
    private auditLogger: AuditLogger
  ) {}

  async executeProposal(request: ExecuteProposalRequest): Promise<ExecutionResult> {
    // 1. Check Idempotency Cache
    if (this.executedKeys.has(request.idempotencyKey)) {
      const prev = this.executedKeys.get(request.idempotencyKey)!;
      return { ...prev, cached: true };
    }

    // 2. Retrieve proposal
    const proposal = this.proposalService.getProposal(request.proposalId);
    if (!proposal) {
      throw new Error(`Proposal ${request.proposalId} not found.`);
    }

    // 3. Verify proposal is still pending
    if (proposal.status !== 'pending') {
      throw new Error(`Proposal ${request.proposalId} is already ${proposal.status}. Cannot execute.`);
    }

    // 4. Verify proposal has not expired
    if (Date.now() > proposal.expiresAt) {
      this.proposalService.updateProposalStatus(request.proposalId, 'expired');
      throw new Error(`Proposal ${request.proposalId} has expired.`);
    }

    // 5. Verify binding exists & status is active
    const binding = this.bindingRegistry.getBinding(proposal.bindingId);
    if (!binding) {
      throw new Error(`Binding ${proposal.bindingId} no longer exists.`);
    }
    if (binding.status !== 'active') {
      throw new Error(`Binding ${proposal.bindingId} is ${binding.status}. Cannot execute proposal.`);
    }

    // 6. Verify binding version has not changed
    if (binding.version !== proposal.bindingVersion) {
      this.proposalService.updateProposalStatus(request.proposalId, 'expired');
      throw new Error(
        `Binding version mismatch: Proposal created under v${proposal.bindingVersion}, current binding is v${binding.version}. Proposal invalidated.`
      );
    }

    // 7. Verify board revision has not changed
    const boardId = (proposal.payload.boardId as string) || 'board-scrum-sprint1';
    const currentRev = await this.kanbanProvider.getBoardRevision(boardId);
    if (currentRev !== proposal.boardRevision) {
      this.proposalService.updateProposalStatus(request.proposalId, 'expired');
      throw new Error(
        `Board revision mismatch: Proposal generated against ${proposal.boardRevision}, current revision is ${currentRev}. Board state updated; proposal invalidated.`
      );
    }

    // 8. Execute action through KanbanProvider
    let createdOrUpdatedTask: KanbanTask | undefined;

    if (proposal.action === 'create_task') {
      createdOrUpdatedTask = await this.kanbanProvider.createTask({
        tenantId: proposal.tenantId,
        boardId,
        title: (proposal.payload.title as string) || 'Untitled Task',
        description: (proposal.payload.description as string) || '',
        status: (proposal.payload.status as KanbanTask['status']) || 'backlog',
        assigneeId: proposal.payload.assigneeId as string,
        blockerRef: proposal.payload.blockerRef as string,
      });
    } else if (proposal.action === 'update_task' && proposal.targetTaskId) {
      createdOrUpdatedTask = await this.kanbanProvider.updateTask(proposal.targetTaskId, {
        title: proposal.payload.title as string,
        status: proposal.payload.status as KanbanTask['status'],
        assigneeId: proposal.payload.assigneeId as string,
        blockerRef: proposal.payload.blockerRef as string,
      });
    } else if (proposal.action === 'add_comment' && proposal.targetTaskId) {
      await this.kanbanProvider.addComment(
        proposal.targetTaskId,
        `Approved by ${request.approvedBy}`,
        (proposal.payload.commentText as string) || proposal.reason
      );
    } else if (proposal.action === 'link_tasks' && proposal.targetTaskId && proposal.payload.targetTaskIdB) {
      await this.kanbanProvider.linkTasks(proposal.targetTaskId, proposal.payload.targetTaskIdB as KanbanTaskId);
    }

    // 9. Update proposal status to executed
    this.proposalService.updateProposalStatus(request.proposalId, 'executed');

    // 10. Log immutable audit event
    this.auditLogger.log({
      taskId: request.proposalId,
      agentId: proposal.agentId,
      scope: {
        tenantId: proposal.tenantId,
        meshId: 'mesh-local',
        roomId: proposal.channelId,
        requesterId: request.approvedBy,
      },
      status: 'completed',
      event: `Proposal ${proposal.proposalId} (${proposal.action}) executed cleanly by approver ${request.approvedBy}.`,
    });

    const result: ExecutionResult = {
      proposalId: request.proposalId,
      idempotencyKey: request.idempotencyKey,
      executedAt: Date.now(),
      task: createdOrUpdatedTask,
      cached: false,
    };

    this.executedKeys.set(request.idempotencyKey, result);
    return result;
  }
}
